import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { queryKeys } from '../query-keys';
import type {
  CheckoutRequest,
  CheckoutResponse,
  CheckinRequest,
  CheckinResponse,
  KioskCheckoutRequest,
  KioskCheckinRequest,
} from '../types/kiosk';

/**
 * Legacy checkout (locationId in request body)
 */
export function useCheckout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CheckoutRequest) =>
      apiFetch<CheckoutResponse>('/api/checkout', {
        method: 'POST',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.active() });
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.all });
    },
  });
}

/**
 * Legacy checkin (locationId in request body)
 */
export function useCheckin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CheckinRequest) =>
      apiFetch<CheckinResponse>('/api/checkin', {
        method: 'POST',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.active() });
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.history() });
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.all });
    },
  });
}

/**
 * Checkout via kiosk device code (location resolved server-side)
 */
export function useCheckoutByCode(kioskCode: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: KioskCheckoutRequest) =>
      apiFetch<CheckoutResponse>(`/api/kiosk/${kioskCode}/checkout`, {
        method: 'POST',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.active() });
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.all });
    },
  });
}

/**
 * Checkin via kiosk device code (location resolved server-side)
 */
export function useCheckinByCode(kioskCode: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: KioskCheckinRequest) =>
      apiFetch<CheckinResponse>(`/api/kiosk/${kioskCode}/checkin`, {
        method: 'POST',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.active() });
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.history() });
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.all });
    },
  });
}
